import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useInventoryData } from '../../lib/admin-hooks';
import { Skeleton } from '../../components/ui/Skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Package, AlertTriangle, ArrowDownRight, ArrowUpRight, Search } from 'lucide-react';
import { formatRelativeTime, cn } from '../../lib/utils';
export function InventoryPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const {
    inventory,
    alerts,
    movements,
    loading: invLoading
  } = useInventoryData(restaurant?.id);
  const [filter, setFilter] = useState<'all' | 'low' | 'out'>('all');
  const [search, setSearch] = useState('');
  const tenantCtx = (() => {
    try {
      const {
        useTenant
      } = require('../../lib/tenant-context');
      return useTenant();
    } catch {
      return null;
    }
  })();
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-724">
        <Skeleton className="w-16 h-16 rounded-full" data-id="element-725" />
      </div>;
  }
  // Derive alert counts from row-level alerts (real view uses uppercase values)
  const criticalCount = alerts.filter(a => a.alert_level === 'CRITICAL').length;
  const lowCount = alerts.filter(a => a.alert_level === 'LOW').length;
  const filteredInventory = inventory.filter(item => {
    const matchesSearch = item.ingredient_name.toLowerCase().includes(search.toLowerCase());
    if (!matchesSearch) return false;
    if (filter === 'out') return item.stock_status === 'out';
    if (filter === 'low') return item.stock_status === 'low' || item.stock_status === 'out';
    return true;
  });
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-726">
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-id="element-727">
        <div data-id="element-728">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-729">Inventario</h1>
          <p className="text-gray-500" data-id="element-730">Control de stock e ingredientes</p>
        </div>

        {/* Alerts Summary */}
        {(criticalCount > 0 || lowCount > 0) && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-731">
            {criticalCount > 0 && <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-4" data-id="element-732">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center shrink-0" data-id="element-733">
                  <AlertTriangle className="w-6 h-6 text-red-600" data-id="element-734" />
                </div>
                <div data-id="element-735">
                  <h3 className="font-bold text-red-900" data-id="element-736">Stock Crítico</h3>
                  <p className="text-red-700 text-sm" data-id="element-737">
                    {criticalCount} ingredientes requieren atención inmediata
                  </p>
                </div>
              </div>}
            {lowCount > 0 && <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-4" data-id="element-738">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center shrink-0" data-id="element-739">
                  <Package className="w-6 h-6 text-amber-600" data-id="element-740" />
                </div>
                <div data-id="element-741">
                  <h3 className="font-bold text-amber-900" data-id="element-742">Stock Bajo</h3>
                  <p className="text-amber-700 text-sm" data-id="element-743">
                    {lowCount} ingredientes por debajo del mínimo sugerido
                  </p>
                </div>
              </div>}
          </div>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-744">
          {/* Main Inventory Table */}
          <Card className="lg:col-span-2" data-id="element-745">
            <CardHeader className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4" data-id="element-746">
              <CardTitle data-id="element-747">Ingredientes</CardTitle>

              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto" data-id="element-748">
                <div className="relative" data-id="element-749">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-750" />
                  <input type="text" placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-amber w-full" data-id="element-751" />
                </div>
                <div className="flex bg-gray-100 p-1 rounded-lg" data-id="element-752">
                  <button onClick={() => setFilter('all')} className={cn('px-3 py-1.5 text-sm font-medium rounded-md transition-colors', filter === 'all' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500')} data-id="element-753">
                    Todos
                  </button>
                  <button onClick={() => setFilter('low')} className={cn('px-3 py-1.5 text-sm font-medium rounded-md transition-colors', filter === 'low' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500')} data-id="element-754">
                    Bajos
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent data-id="element-755">
              {invLoading ? <div className="space-y-4" data-id="element-756">
                  {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-12 w-full" data-id="element-757" />)}
                </div> : <div className="overflow-x-auto" data-id="element-758">
                  <table className="w-full text-sm text-left" data-id="element-759">
                    <thead className="text-xs text-gray-500 uppercase bg-gray-50" data-id="element-760">
                      <tr data-id="element-761">
                        <th className="px-4 py-3 rounded-l-lg" data-id="element-762">Ingrediente</th>
                        <th className="px-4 py-3 text-right" data-id="element-763">Stock Actual</th>
                        <th className="px-4 py-3 text-right" data-id="element-764">Mínimo</th>
                        <th className="px-4 py-3 rounded-r-lg" data-id="element-765">Estado</th>
                      </tr>
                    </thead>
                    <tbody data-id="element-766">
                      {filteredInventory.map(item => <tr key={item.inventory_item_id} className="border-b border-gray-50 last:border-0" data-id="element-767">
                          <td className="px-4 py-3 font-medium text-gray-900" data-id="element-768">
                            {item.ingredient_name}
                            {item.supplier && <div className="text-xs text-gray-400 font-normal" data-id="element-769">
                                {item.supplier}
                              </div>}
                          </td>
                          <td className="px-4 py-3 text-right font-bold" data-id="element-770">
                            {item.current_stock}{' '}
                            <span className="text-gray-500 font-normal" data-id="element-771">
                              {item.unit}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500" data-id="element-772">
                            {item.minimum_stock} {item.unit}
                          </td>
                          <td className="px-4 py-3" data-id="element-773">
                            {item.stock_status === 'ok' ? <Badge variant="success" data-id="element-774">OK</Badge> : item.stock_status === 'low' ? <Badge variant="warning" data-id="element-775">Bajo</Badge> : <Badge variant="danger" data-id="element-776">Agotado</Badge>}
                          </td>
                        </tr>)}
                      {filteredInventory.length === 0 && <tr data-id="element-777">
                          <td colSpan={4} className="px-4 py-8 text-center text-gray-500" data-id="element-778">
                            No se encontraron ingredientes
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>}
            </CardContent>
          </Card>

          {/* Recent Movements */}
          <Card data-id="element-779">
            <CardHeader data-id="element-780">
              <CardTitle data-id="element-781">Movimientos Recientes</CardTitle>
            </CardHeader>
            <CardContent data-id="element-782">
              {invLoading ? <Skeleton className="h-64 w-full" data-id="element-783" /> : movements.length > 0 ? <div className="space-y-4" data-id="element-784">
                  {movements.map(mov => {
                // Find item name from inventory view
                const item = inventory.find(i => i.inventory_item_id === mov.inventory_item_id);
                const isDeduction = mov.movement_type === 'sale_consumption' || mov.movement_type === 'waste' || mov.movement_type === 'manual_adjustment';
                const isIncrease = mov.movement_type === 'restock';
                return <div key={mov.id} className="flex items-start gap-3 pb-4 border-b border-gray-50 last:border-0 last:pb-0" data-id="element-785">
                        <div className={cn('w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5', isIncrease ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600')} data-id="element-786">
                          {isIncrease ? <ArrowUpRight className="w-4 h-4" data-id="element-787" /> : <ArrowDownRight className="w-4 h-4" data-id="element-788" />}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-789">
                          <p className="text-sm font-medium text-gray-900 truncate" data-id="element-790">
                            {item?.ingredient_name || 'Item desconocido'}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-791">
                            {mov.movement_type === 'sale_consumption' ? 'Consumo por venta' : mov.movement_type === 'restock' ? 'Reabastecimiento' : mov.movement_type === 'waste' ? 'Desperdicio' : 'Ajuste manual'}
                            {mov.reason && ` • ${mov.reason}`}
                          </p>
                        </div>
                        <div className={cn('text-sm font-bold shrink-0', isIncrease ? 'text-emerald-600' : 'text-red-600')} data-id="element-792">
                          {isIncrease ? '+' : '-'}
                          {Math.abs(mov.quantity)} {item?.unit}
                        </div>
                      </div>;
              })}
                </div> : <p className="text-sm text-gray-500 text-center py-4" data-id="element-793">
                  No hay movimientos recientes
                </p>}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>;
}